import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useLocation, useNavigate } from 'react-router-dom'
import styled, { keyframes } from 'styled-components'
import {
    Check,
    ClockCounterClockwise,
    EnvelopeSimple,
    Warning,
    WarningCircle,
    X,
} from 'phosphor-react'
import { useNotification } from 'hooks/toast'
import { useTabletAndBelowMediaQuery } from 'layout'
import { CustomerEmailModal } from 'page-components/customer/CustomerEmailModal'
import {
    EMAIL_HISTORY_FILTERS,
    EMAIL_HISTORY_PAGE_SIZE,
    InspectionService,
    buildSignInUrlWithReturn,
    dedupeEmailHistory,
    emailHistoryErrorKind,
    emailMatchesFilter,
    emailTypeGroup,
    formatEmailSentOn,
    normalizeAuditEmailPage,
    splitAddresses,
} from 'utility'

const shimmer = keyframes`
    0% { background-position: 100% 0; }
    100% { background-position: -100% 0; }
`

const Overlay = styled.div`
    position: fixed;
    inset: 0;
    z-index: ${props => (props.$behind ? 900 : 1400)};
    background: rgba(15, 23, 42, 0.45);
    display: flex;
    justify-content: flex-end;
`

const Panel = styled.aside`
    width: ${props => (props.$sheet ? '100%' : 'min(520px, 100%)')};
    height: 100%;
    height: 100dvh;
    background: #ffffff;
    color: #1f2430;
    display: flex;
    flex-direction: column;
    box-shadow: ${props =>
        props.$sheet ? 'none' : '-12px 0 40px rgba(15, 23, 42, 0.18)'};
    animation: ${props => (props.$sheet ? 'none' : 'emailHistoryIn 180ms ease')};

    @keyframes emailHistoryIn {
        from {
            transform: translateX(16px);
            opacity: 0.6;
        }
        to {
            transform: translateX(0);
            opacity: 1;
        }
    }

    @media (prefers-reduced-motion: reduce) {
        animation: none;
    }
`

const Header = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    padding: 18px 18px 14px;
    border-bottom: 1px solid #e6e8ef;
`

const TitleBlock = styled.div`
    min-width: 0;
`

const Title = styled.h2`
    margin: 0;
    font-size: 18px;
    line-height: 1.3;
    font-weight: 700;
    color: #1f2430;
`

const Subtitle = styled.p`
    margin: 4px 0 0;
    font-size: 13px;
    color: #6b7280;
`

const HeaderActions = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
`

const SendEmailButton = styled.button`
    border: 0;
    background: #263ba6;
    color: #ffffff;
    border-radius: 8px;
    padding: 8px 12px;
    font-size: 13px;
    font-weight: 700;
    font-family: inherit;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 6px;

    &:disabled {
        opacity: 0.65;
        cursor: default;
    }
`

const CloseButton = styled.button`
    border: 0;
    background: #f3f4f6;
    color: #374151;
    width: 36px;
    height: 36px;
    border-radius: 10px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    flex-shrink: 0;

    &:hover {
        background: #e5e7eb;
    }
`

const Filters = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    padding: 12px 18px;
    border-bottom: 1px solid #eef0f4;
`

const FilterChip = styled.button`
    border: 1px solid ${props => (props.$active ? '#263BA6' : '#e1e4ee')};
    background: ${props => {
        if (!props.$active) return '#ffffff'
        return props.$danger ? '#fef2f2' : '#263BA6'
    }};
    color: ${props => {
        if (!props.$active) return '#374151'
        return props.$danger ? '#b91c1c' : '#ffffff'
    }};
    border-color: ${props => {
        if (props.$active && props.$danger) return '#fecaca'
        if (props.$active) return '#263BA6'
        return '#e1e4ee'
    }};
    border-radius: 999px;
    padding: 6px 12px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    font-family: inherit;
`

const List = styled.div`
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 14px 18px 18px;
`

const EmailCard = styled.article`
    border: 1px solid #e6e8ef;
    border-radius: 12px;
    padding: 12px 14px;
    margin-bottom: 10px;
    background: #ffffff;
`

const CardTop = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
`

const TypeChip = styled.span`
    display: inline-flex;
    align-items: center;
    max-width: 70%;
    padding: 3px 8px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 700;
    line-height: 1.3;
    background: ${props => props.$bg};
    color: ${props => props.$color};
    overflow-wrap: anywhere;
`

const Status = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    font-weight: 700;
    color: ${props => (props.$failed ? '#b91c1c' : '#15803d')};
    white-space: nowrap;
`

const When = styled.div`
    margin-top: 8px;
    font-size: 12px;
    color: #6b7280;
`

const Subject = styled.div`
    margin-top: 6px;
    font-size: 14px;
    font-weight: 600;
    line-height: 1.4;
    overflow-wrap: anywhere;
    word-break: break-word;
    white-space: pre-wrap;
`

const AddressLine = styled.div`
    margin-top: 6px;
    font-size: 13px;
    color: #4b5563;
    overflow-wrap: anywhere;
    word-break: break-word;
    white-space: pre-wrap;
`

const MoreButton = styled.button`
    border: 0;
    background: transparent;
    color: #263BA6;
    font-weight: 700;
    cursor: pointer;
    padding: 0 0 0 6px;
    font-family: inherit;
    font-size: 13px;
`

const ErrorBox = styled.div`
    margin-top: 8px;
    background: #fde8e8;
    color: #9f1239;
    border-radius: 8px;
    padding: 8px 10px;
    font-size: 13px;
    overflow-wrap: anywhere;
    word-break: break-word;
    white-space: pre-wrap;
`

const Footer = styled.div`
    padding: 12px 18px 16px;
    border-top: 1px solid #eef0f4;
    font-size: 12px;
    line-height: 1.45;
    color: #6b7280;
`

const StateBox = styled.div`
    min-height: 220px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    gap: 8px;
    color: #4b5563;
    padding: 24px 12px;
`

const StateTitle = styled.div`
    font-size: 16px;
    font-weight: 700;
    color: #1f2430;
`

const StateText = styled.p`
    margin: 0;
    max-width: 320px;
    font-size: 14px;
    line-height: 1.45;
`

const RetryButton = styled.button`
    margin-top: 8px;
    border: 0;
    background: #263BA6;
    color: #ffffff;
    border-radius: 8px;
    padding: 8px 14px;
    font-weight: 700;
    cursor: pointer;
    font-family: inherit;
`

const LoadMore = styled.button`
    width: 100%;
    margin-top: 4px;
    border: 1px solid #d5d9e6;
    background: #ffffff;
    color: #263BA6;
    border-radius: 10px;
    padding: 10px 12px;
    font-weight: 700;
    cursor: pointer;
    font-family: inherit;

    &:disabled {
        cursor: default;
        color: #6b7280;
    }
`

const InlineError = styled.div`
    margin-top: 8px;
    text-align: center;
    color: #9f1239;
    font-size: 13px;
`

const SkeletonCard = styled.div`
    border: 1px solid #eef0f4;
    border-radius: 12px;
    padding: 14px;
    margin-bottom: 10px;
`

const SkeletonBar = styled.div`
    height: ${props => props.$height || 12}px;
    width: ${props => props.$width || '100%'};
    border-radius: 999px;
    margin-top: ${props => props.$top || 0}px;
    background: linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 37%, #f3f4f6 63%);
    background-size: 400% 100%;
    animation: ${shimmer} 1.4s ease infinite;

    @media (prefers-reduced-motion: reduce) {
        animation: none;
    }
`

const HistoryTrigger = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    border: 0;
    background: transparent;
    padding: 0;
    margin: ${props => (props.$block ? '0' : '0 1rem 0 0')};
    cursor: pointer;
    color: inherit;
    font: inherit;
    line-height: inherit;
    text-align: left;
`

const CountBadge = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 22px;
    height: 22px;
    padding: 0 7px;
    border-radius: 999px;
    border: 1.5px solid #263BA6;
    background: #eef1fb;
    color: #263BA6;
    font-size: 12px;
    font-weight: 700;
    line-height: 1;
`

const GROUP_STYLE = {
    report: { bg: '#e8edff', color: '#263BA6' },
    tickets: { bg: '#f3e8ff', color: '#6d28d9' },
    workflow: { bg: '#e6f7f4', color: '#0f766e' },
    other: { bg: '#f3f4f6', color: '#374151' },
}

const ACCESS_DENIED = "You don't have access to this audit"
const LOAD_ERROR = "Couldn't load the email history. Try again."

const AddressText = ({ label, value, expanded, onToggle }) => {
    const addresses = splitAddresses(value)
    if (!addresses.length) return null
    const hidden = expanded ? 0 : Math.max(addresses.length - 3, 0)
    const visible = expanded ? addresses : addresses.slice(0, 3)
    return (
        <AddressLine>
            {label}: {visible.join(', ')}
            {hidden > 0 && (
                <MoreButton type="button" onClick={onToggle}>
                    +{hidden} more
                </MoreButton>
            )}
            {expanded && addresses.length > 3 && (
                <MoreButton type="button" onClick={onToggle}>
                    Show less
                </MoreButton>
            )}
        </AddressLine>
    )
}

const EmailRow = ({ item, expanded, onToggle }) => {
    const group = emailTypeGroup(item.type)
    const tone = GROUP_STYLE[group] || GROUP_STYLE.other
    const when = formatEmailSentOn(item.sentOnUtc)
    return (
        <EmailCard>
            <CardTop>
                <TypeChip $bg={tone.bg} $color={tone.color}>
                    {item.typeLabel || 'Email'}
                </TypeChip>
                <Status $failed={!item.sent}>
                    {item.sent ? <Check size={14} weight="bold" /> : <X size={14} weight="bold" />}
                    {item.sent ? 'Sent' : 'Failed'}
                </Status>
            </CardTop>
            {when && <When>{when}</When>}
            <Subject>{item.subject || 'No subject'}</Subject>
            <AddressText
                label="To"
                value={item.to}
                expanded={!!expanded.to}
                onToggle={() => onToggle('to')}
            />
            <AddressText
                label="CC"
                value={item.cc}
                expanded={!!expanded.cc}
                onToggle={() => onToggle('cc')}
            />
            {!item.sent && item.error ? <ErrorBox>{item.error}</ErrorBox> : null}
        </EmailCard>
    )
}

const LoadingRows = () => (
    <>
        {[0, 1, 2].map(row => (
            <SkeletonCard key={row}>
                <SkeletonBar $width="42%" $height={14} />
                <SkeletonBar $width="55%" $height={10} $top={10} />
                <SkeletonBar $width="88%" $height={12} $top={10} />
                <SkeletonBar $width="70%" $height={10} $top={10} />
            </SkeletonCard>
        ))}
    </>
)

export const useAuditEmailHistory = auditId => {
    const navigate = useNavigate()
    const location = useLocation()
    const [open, setOpen] = useState(false)
    const [refreshKey, setRefreshKey] = useState(0)
    const [meta, setMeta] = useState({
        totalCount: null,
        historyAvailable: true,
    })
    const seenAuditId = useRef(auditId)

    useEffect(() => {
        if (seenAuditId.current !== auditId) {
            seenAuditId.current = auditId
            setOpen(false)
            setMeta({ totalCount: null, historyAvailable: true })
        }
        if (!auditId) return undefined

        let cancelled = false
        const loadBadge = async () => {
            try {
                const result = await InspectionService.getAuditEmails(auditId, {
                    page: 1,
                    pageSize: 1,
                })
                if (cancelled) return
                const page = normalizeAuditEmailPage(result, 1)
                setMeta({
                    totalCount: page.historyAvailable ? page.totalCount : null,
                    historyAvailable: page.historyAvailable,
                })
            } catch (err) {
                if (cancelled) return
                if (emailHistoryErrorKind(err) === 'unauthorized') {
                    navigate(
                        buildSignInUrlWithReturn(
                            location.pathname,
                            location.search,
                        ),
                    )
                    return
                }
                setMeta({ totalCount: null, historyAvailable: true })
            }
        }

        loadBadge()
        return () => {
            cancelled = true
        }
    }, [auditId, refreshKey, navigate, location.pathname, location.search])

    const refresh = useCallback(() => {
        setRefreshKey(key => key + 1)
    }, [])

    return {
        open,
        setOpen,
        refreshKey,
        refresh,
        totalCount: meta.totalCount,
        historyAvailable: meta.historyAvailable,
        setMeta,
    }
}

export const EmailHistoryToolbarButton = ({
    totalCount,
    historyAvailable = true,
    onClick,
    block = false,
}) => {
    const showBadge =
        historyAvailable && totalCount != null && Number.isFinite(Number(totalCount))
    return (
        <HistoryTrigger type="button" onClick={onClick} $block={block}>
            <ClockCounterClockwise size={18} />
            <span>Email history</span>
            {showBadge && <CountBadge>{totalCount}</CountBadge>}
        </HistoryTrigger>
    )
}

export const EmailHistoryPanel = ({
    open,
    auditId,
    refreshKey = 0,
    knownTotal = null,
    onClose,
    onMeta,
    showSendEmail = false,
}) => {
    const navigate = useNavigate()
    const location = useLocation()
    const { errorToast } = useNotification()
    const isSheet = useTabletAndBelowMediaQuery()
    const closeRef = useRef(null)
    const requestRef = useRef(0)
    const sessionRef = useRef({ page: 0, items: [] })
    const onCloseRef = useRef(onClose)
    const onMetaRef = useRef(onMeta)
    const navigateRef = useRef(navigate)
    const locationRef = useRef(location)
    const errorToastRef = useRef(errorToast)
    onCloseRef.current = onClose
    onMetaRef.current = onMeta
    navigateRef.current = navigate
    locationRef.current = location
    errorToastRef.current = errorToast

    const [items, setItems] = useState([])
    const [totalCount, setTotalCount] = useState(null)
    const [page, setPage] = useState(0)
    const [historyAvailable, setHistoryAvailable] = useState(true)
    const [loading, setLoading] = useState(false)
    const [loadingMore, setLoadingMore] = useState(false)
    const [error, setError] = useState('')
    const [loadMoreError, setLoadMoreError] = useState(false)
    const [filter, setFilter] = useState('all')
    const [expanded, setExpanded] = useState({})
    const [exhausted, setExhausted] = useState(false)
    const [sendOpen, setSendOpen] = useState(false)
    const [sendReportId, setSendReportId] = useState(null)
    const [openingSend, setOpeningSend] = useState(false)
    const sendOpenRef = useRef(false)
    sendOpenRef.current = sendOpen

    const close = useCallback(() => {
        onCloseRef.current?.()
    }, [])

    useEffect(() => {
        if (!open) {
            setFilter('all')
            setExpanded({})
            return undefined
        }
        const previousOverflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        closeRef.current?.focus()
        const onKey = event => {
            if (sendOpenRef.current) return
            if (event.key === 'Escape') onCloseRef.current?.()
        }
        document.addEventListener('keydown', onKey)
        return () => {
            document.body.style.overflow = previousOverflow
            document.removeEventListener('keydown', onKey)
        }
    }, [open])

    const loadPage = useCallback(
        async ({ requestedPage, append }) => {
            if (!auditId) return
            const requestId = requestRef.current + 1
            requestRef.current = requestId
            if (append) {
                setLoadingMore(true)
                setLoadMoreError(false)
            } else {
                sessionRef.current = { page: 0, items: [] }
                setItems([])
                setLoading(true)
                setError('')
                setExhausted(false)
                setHistoryAvailable(true)
            }
            try {
                const result = await InspectionService.getAuditEmails(auditId, {
                    page: requestedPage,
                    pageSize: EMAIL_HISTORY_PAGE_SIZE,
                })
                if (requestRef.current !== requestId) return
                const pageData = normalizeAuditEmailPage(result, requestedPage)
                if (!pageData.historyAvailable) {
                    sessionRef.current = { page: requestedPage, items: [] }
                    setHistoryAvailable(false)
                    setItems([])
                    setTotalCount(0)
                    setPage(requestedPage)
                    setError('')
                    onMetaRef.current?.({
                        totalCount: null,
                        historyAvailable: false,
                    })
                    return
                }
                const previous = append ? sessionRef.current.items : []
                const next = append
                    ? dedupeEmailHistory([...previous, ...pageData.items])
                    : pageData.items
                if (append && next.length === previous.length) {
                    setExhausted(true)
                }
                sessionRef.current = { page: requestedPage, items: next }
                setHistoryAvailable(true)
                setItems(next)
                setTotalCount(pageData.totalCount)
                setPage(requestedPage)
                setError('')
                onMetaRef.current?.({
                    totalCount: pageData.totalCount,
                    historyAvailable: true,
                })
            } catch (err) {
                if (requestRef.current !== requestId) return
                const kind = emailHistoryErrorKind(err)
                if (kind === 'unauthorized') {
                    onCloseRef.current?.()
                    navigateRef.current(
                        buildSignInUrlWithReturn(
                            locationRef.current.pathname,
                            locationRef.current.search,
                        ),
                    )
                    return
                }
                if (kind === 'not-found') {
                    onCloseRef.current?.()
                    errorToastRef.current(ACCESS_DENIED)
                    return
                }
                if (append) {
                    setLoadMoreError(true)
                    return
                }
                setError(LOAD_ERROR)
            } finally {
                if (requestRef.current === requestId) {
                    setLoading(false)
                    setLoadingMore(false)
                }
            }
        },
        [auditId],
    )

    useEffect(() => {
        if (!open || !auditId) return undefined
        loadPage({ requestedPage: 1, append: false })
        return () => {
            requestRef.current += 1
        }
    }, [open, auditId, refreshKey, loadPage])

    const filtered = useMemo(
        () => items.filter(item => emailMatchesFilter(item, filter)),
        [items, filter],
    )

    const hasMore =
        historyAvailable &&
        !exhausted &&
        totalCount != null &&
        items.length < totalCount

    if (!open) return null

    const displayCount = totalCount != null ? totalCount : knownTotal
    const countLabel =
        historyAvailable && !error && displayCount != null
            ? `${displayCount} ${Number(displayCount) === 1 ? 'email' : 'emails'}`
            : ''

    const showFilters = !loading && !error && historyAvailable
    const showFooter = !loading && !error && historyAvailable

    let body = null
    if (loading && items.length === 0) {
        body = <LoadingRows />
    } else if (error) {
        body = (
            <StateBox>
                <WarningCircle size={28} color="#b91c1c" />
                <StateTitle>Couldn&apos;t load email history</StateTitle>
                <StateText>{error}</StateText>
                <RetryButton
                    type="button"
                    onClick={() => loadPage({ requestedPage: 1, append: false })}
                >
                    Retry
                </RetryButton>
            </StateBox>
        )
    } else if (!historyAvailable) {
        body = (
            <StateBox>
                <Warning size={28} color="#b45309" />
                <StateTitle>Not available</StateTitle>
                <StateText>Email history isn&apos;t available yet.</StateText>
            </StateBox>
        )
    } else if (!loading && items.length === 0) {
        body = (
            <StateBox>
                <EnvelopeSimple size={28} color="#263BA6" />
                <StateTitle>No emails yet</StateTitle>
                <StateText>
                    No emails have been sent for this audit since email history
                    started.
                </StateText>
            </StateBox>
        )
    } else if (filtered.length === 0) {
        body = (
            <StateBox>
                <EnvelopeSimple size={28} color="#6b7280" />
                <StateTitle>No matching emails</StateTitle>
                <StateText>
                    None of the loaded emails match this filter.
                </StateText>
            </StateBox>
        )
    } else {
        body = filtered.map(item => (
            <EmailRow
                key={item.emailLogId}
                item={item}
                expanded={expanded[item.emailLogId] || {}}
                onToggle={field =>
                    setExpanded(current => ({
                        ...current,
                        [item.emailLogId]: {
                            ...(current[item.emailLogId] || {}),
                            [field]: !current[item.emailLogId]?.[field],
                        },
                    }))
                }
            />
        ))
    }

    const openSendEmail = async () => {
        if (!auditId || openingSend) return
        setOpeningSend(true)
        try {
            const result = await InspectionService.getReport(auditId)
            const reportId = result?.Id ?? result?.id
            if (!reportId) {
                errorToastRef.current('Report ID is missing')
                return
            }
            setSendReportId(reportId)
            setSendOpen(true)
        } catch {
            errorToastRef.current('Failed to open send email')
        } finally {
            setOpeningSend(false)
        }
    }

    const closeSendEmail = shouldRefresh => {
        setSendOpen(false)
        document.body.style.overflow = 'hidden'
        if (shouldRefresh) {
            loadPage({ requestedPage: 1, append: false })
        }
    }

    return createPortal(
        <Overlay
            $behind={sendOpen}
            onMouseDown={() => {
                if (sendOpenRef.current) return
                close()
            }}
        >
            <Panel
                $sheet={isSheet}
                role="dialog"
                aria-modal="true"
                aria-labelledby="email-history-title"
                onMouseDown={event => event.stopPropagation()}
            >
                <Header>
                    <TitleBlock>
                        <Title id="email-history-title">Email history</Title>
                        {countLabel ? <Subtitle>{countLabel}</Subtitle> : null}
                    </TitleBlock>
                    <HeaderActions>
                        {showSendEmail && (
                            <SendEmailButton
                                type="button"
                                onClick={openSendEmail}
                                disabled={openingSend}
                            >
                                <EnvelopeSimple size={16} />
                                {openingSend ? 'Opening…' : 'Send Email'}
                            </SendEmailButton>
                        )}
                        <CloseButton
                            ref={closeRef}
                            type="button"
                            aria-label="Close email history"
                            onClick={close}
                        >
                            <X size={18} />
                        </CloseButton>
                    </HeaderActions>
                </Header>
                {showFilters && (
                    <Filters>
                        {EMAIL_HISTORY_FILTERS.map(option => (
                            <FilterChip
                                key={option.id}
                                type="button"
                                $active={filter === option.id}
                                $danger={option.id === 'failed'}
                                aria-pressed={filter === option.id}
                                onClick={() => setFilter(option.id)}
                            >
                                {option.label}
                            </FilterChip>
                        ))}
                    </Filters>
                )}
                <List>
                    {body}
                    {showFooter && hasMore && (
                        <LoadMore
                            type="button"
                            onClick={() =>
                                loadPage({
                                    requestedPage: page + 1,
                                    append: true,
                                })
                            }
                            disabled={loadingMore}
                        >
                            {loadingMore ? 'Loading…' : 'Load more'}
                        </LoadMore>
                    )}
                    {loadMoreError && (
                        <InlineError>
                            {LOAD_ERROR}{' '}
                            <MoreButton
                                type="button"
                                onClick={() =>
                                    loadPage({
                                        requestedPage: page + 1,
                                        append: true,
                                    })
                                }
                            >
                                Retry
                            </MoreButton>
                        </InlineError>
                    )}
                </List>
                {showFooter && (
                    <Footer>
                        Times in your local time. &quot;Sent&quot; = accepted by
                        our mail server; delivery to the recipient&apos;s inbox
                        is not tracked.
                    </Footer>
                )}
            </Panel>
            {showSendEmail && (
                <CustomerEmailModal
                    open={sendOpen}
                    onClose={closeSendEmail}
                    reportId={sendReportId}
                />
            )}
        </Overlay>,
        document.body,
    )
}
